import type { TFunction } from 'i18next';
import type { Post, PostResult } from '@/shared/services/posts.service';
import { isFailedResult, platformName } from '@/shared/lib/post-result-error';
import { formatTime } from '@/shared/lib/intl';

/** One channel on a receipt, in the order the post lists its channels. */
export interface ReceiptLine {
  platform: string;
  state: 'published' | 'failed' | 'pending';
  result: PostResult | null;
}

export interface Receipt {
  lines: ReceiptLine[];
  published: number;
  total: number;
  /** The channels that did not go out, for the failure section. */
  failed: PostResult[];
  /** A channel no longer has the post (the 10-minute check). */
  gone: PostResult[];
}

/** A post's receipt, from its results: nothing is stored twice (Task 1.3). */
export function buildReceipt(post: Pick<Post, 'platforms' | 'results'>): Receipt {
  const results = post.results ?? [];
  const lines: ReceiptLine[] = post.platforms.map((platform) => {
    const result = results.find((r) => r.platform.toUpperCase() === platform.toUpperCase()) ?? null;
    const state = result?.status === 'PUBLISHED' ? 'published' : result && isFailedResult(result) ? 'failed' : 'pending';
    return { platform, state, result };
  });
  return {
    lines,
    published: lines.filter((l) => l.state === 'published').length,
    total: lines.length,
    failed: lines.filter((l) => l.state === 'failed' && l.result).map((l) => l.result as PostResult),
    gone: results.filter((r) => r.status === 'PUBLISHED' && r.verification === 'gone'),
  };
}

/** "Went out on 4 of 4 channels", "3 of 4: Pinterest needs you", or "Did not go out". */
export function receiptHeadline(receipt: Receipt, t: TFunction): string {
  if (receipt.published === 0) return String(t(receipt.lines.some((l) => l.state === 'pending') ? 'receipt.pending' : 'receipt.failedHeadline'));
  const firstFailed = receipt.failed[0];
  if (firstFailed) {
    return String(t('receipt.partialHeadline', { published: receipt.published, total: receipt.total, platform: platformName(firstFailed.platform) }));
  }
  return String(t('receipt.headline', { published: receipt.published, count: receipt.total }));
}

/** "Verified on Instagram · 10:42" for the check that ran. */
export function verifiedLine(result: PostResult, t: TFunction): string | null {
  if (result.verification !== 'verified' || !result.verifiedAt) return null;
  return String(t('receipt.verified', { platform: platformName(result.platform), time: formatTime(result.verifiedAt) }));
}

/** Posts that have a receipt: every channel reported, or some went out. */
export function hasReceipt(status: string): boolean {
  return ['PUBLISHED', 'PARTIAL', 'FAILED'].includes(status.toUpperCase());
}
